import React, { useState, useEffect, useRef } from 'react';
import './AccessibilityToggle.css';

interface Preferences {
    highContrast: boolean;
    largeText: boolean;
    reducedMotion: boolean;
    screenReader: boolean;
    keyboardNav: boolean;
}

const defaultPreferences: Preferences = {
    highContrast: false,
    largeText: false,
    reducedMotion: false,
    screenReader: false,
    keyboardNav: false,
};

const AccessibilityToggle: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const panelRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const [accessibilityMode, setAccessibilityMode] = useState(() => {
        return localStorage.getItem('accessibility-mode') === 'enhanced';
    });

    const [preferences, setPreferences] = useState<Preferences>(() => {
        const saved = localStorage.getItem('accessibility-preferences');
        return saved ? JSON.parse(saved) : defaultPreferences;
    });

    useEffect(() => {
        if (accessibilityMode) {
            document.body.classList.add('accessibility-enhanced');
            localStorage.setItem('accessibility-mode', 'enhanced');
        } else {
            document.body.classList.remove('accessibility-enhanced');
            localStorage.setItem('accessibility-mode', 'standard');
        }
    }, [accessibilityMode]);

    useEffect(() => {
        const body = document.body;
        body.classList.toggle('high-contrast', preferences.highContrast);
        body.classList.toggle('large-text', preferences.largeText);
        body.classList.toggle('reduced-motion', preferences.reducedMotion);
        body.classList.toggle('screen-reader-optimized', preferences.screenReader);
        body.classList.toggle('keyboard-navigation', preferences.keyboardNav);
        localStorage.setItem('accessibility-preferences', JSON.stringify(preferences));
    }, [preferences]);

    // Close panel on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                panelRef.current &&
                !panelRef.current.contains(e.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(e.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
                buttonRef.current?.focus();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen]);

    const updatePreference = (key: keyof Preferences) => {
        setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const options: { key: keyof Preferences; label: string; description: string }[] = [
        { key: 'highContrast', label: 'High Contrast', description: 'Increases color contrast for better readability' },
        { key: 'largeText', label: 'Large Text', description: 'Increases text size throughout the interface' },
        { key: 'reducedMotion', label: 'Reduce Motion', description: 'Minimizes animations and transitions' },
        { key: 'screenReader', label: 'Screen Reader Mode', description: 'Optimizes layout and content for screen readers' },
        { key: 'keyboardNav', label: 'Enhanced Keyboard Navigation', description: 'Adds visible focus indicators and keyboard shortcuts' },
    ];

    return (
        <div className="a11y-floating-container">
            <button
                ref={buttonRef}
                className="a11y-floating-button"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Accessibility settings"
                aria-expanded={isOpen}
                aria-controls="a11y-settings-panel"
            >
                <span aria-hidden="true">&#x267F;</span>
            </button>

            {isOpen && (
                <div
                    ref={panelRef}
                    id="a11y-settings-panel"
                    className="a11y-settings-panel"
                    role="dialog"
                    aria-label="Accessibility Settings"
                >
                    <div className="a11y-panel-header">Accessibility Settings</div>

                    <div className="a11y-option">
                        <label className="a11y-switch">
                            <input
                                type="checkbox"
                                checked={accessibilityMode}
                                onChange={() => setAccessibilityMode(!accessibilityMode)}
                                aria-describedby="a11y-enhanced-desc"
                            />
                            <span className="a11y-slider"></span>
                            <span className="a11y-label">Enhanced Mode</span>
                        </label>
                        <small id="a11y-enhanced-desc" className="a11y-description">
                            Optimizes the entire interface for better accessibility
                        </small>
                    </div>

                    <hr className="a11y-divider" />

                    <div className="a11y-panel-subheader">Individual Settings</div>

                    {options.map(opt => (
                        <div key={opt.key} className="a11y-option">
                            <label className="a11y-switch">
                                <input
                                    type="checkbox"
                                    checked={preferences[opt.key]}
                                    onChange={() => updatePreference(opt.key)}
                                    aria-describedby={`a11y-${opt.key}-desc`}
                                />
                                <span className="a11y-slider"></span>
                                <span className="a11y-label">{opt.label}</span>
                            </label>
                            <small id={`a11y-${opt.key}-desc`} className="a11y-description">
                                {opt.description}
                            </small>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AccessibilityToggle;
